import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { forgotPassword } from '../services/api';
import { AUTH_CSS, BrandLogo } from './AuthPage';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValidEmail || loading) return;
    setError('');
    setLoading(true);
    try {
      await forgotPassword(email);
      setSent(true);
    } catch (err) {
      const msg = err?.message || '';
      if (msg.toLowerCase().includes('not registered')) {
        setError('Email not registered');
      } else {
        setError(msg || 'Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-root">
      <style>{AUTH_CSS}</style>
      <Link to="/login" className="auth-back-link">← Back to login</Link>

      <div className="auth-center">
        <div className="auth-identity">
          <BrandLogo />
          <span className="auth-identity__name">DS CodeXAI</span>
          <span className="auth-identity__tag">Autonomous Software Engineering Platform</span>
        </div>

        <div className="auth-card">
          {!sent ? (
            <>
              <div className="auth-card__header">
                <h2 className="auth-card__title">Reset your password</h2>
                <p className="auth-card__sub">
                  Enter your email and we'll send you a link to reset your password.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="auth-form">
                <div className="auth-field">
                  <label className="auth-label">Email</label>
                  <input
                    ref={inputRef}
                    type="email"
                    value={email}
                    onChange={e => { setEmail(e.target.value); setError(''); }}
                    placeholder="you@company.com"
                    required
                    className="auth-input"
                  />
                  {email && !isValidEmail && (
                    <span className="auth-field-error">Please enter a valid email address</span>
                  )}
                </div>

                {error && (
                  <div className="auth-error">
                    <span className="auth-error__icon">⚠</span>
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={!isValidEmail || loading}
                  className={`auth-btn${loading ? ' auth-btn--loading' : ''}`}
                >
                  {loading ? 'Sending…' : 'Send Reset Link'}
                </button>
              </form>

              <p className="auth-switch">
                Remember your password?{' '}
                <Link to="/login" className="auth-switch__link" style={{ textDecoration: 'none' }}>Sign in</Link>
              </p>
            </>
          ) : (
            <div className="auth-success">
              <div className="auth-success__icon">
                <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                  <circle cx="24" cy="24" r="24" fill="rgba(99, 102, 241, 0.12)" />
                  <path d="M15 25l6 6 12-12" stroke="#6366f1" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <h2 className="auth-card__title" style={{ textAlign: 'center' }}>Check your email</h2>
              <p className="auth-success__msg">
                If an account exists with this email, we've sent a password reset link.
              </p>
              <Link to="/login" className="auth-btn auth-success__btn">
                Return to Login
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
